import React from 'react'
import { Route, Switch } from 'wouter'
import { ThemeProvider } from './components/theme-provider'
import { AuthProvider } from './contexts/auth-context'
import { captureGclidFromUrl } from './lib/gclid'
import { Layout } from './components/layout'
import { IntercomComponent } from './components/intercom'
import { Dashboard } from './pages/dashboard'
import { AllRfps } from './pages/all-rfps'
import { CreateRfp } from './pages/create-rfp'
import { RfpDetail } from './pages/rfp-detail'
import { Login } from './pages/login'
import { Register } from './pages/register'
import { AuthCallback } from './pages/auth-callback'
import { ResetPasswordRequest } from './pages/reset-password-request'
import { ResetPassword } from './pages/reset-password'
import { ProtectedRoute } from './components/protected-route'
import { Terms } from './pages/terms'
import { Privacy } from './pages/privacy'

import { UserProfile } from './pages/user-profile'
import { RespondToRfp } from './pages/respond-to-rfp'
import { RfpResponses } from './pages/rfp-responses'
import { Watchlist } from './pages/watchlist'
import { MyRfps } from './pages/my-rfps'
import { MyResponses } from './pages/my-responses'
import { EditRfp } from './pages/edit-rfp'
import { EditResponse } from './pages/edit-response'
import { Welcome } from './pages/welcome'
import { StructuredDataTest } from './pages/structured-data-test'

// SEO Pages
import { SEOHomepage } from './pages/seo/homepage'
import { AboutUs } from './pages/seo/about-us'
import { HowItWorks } from './pages/seo/how-it-works'
import { Pricing } from './pages/seo/pricing'
import { SuccessStories } from './pages/seo/success-stories'
import { Blog } from './pages/seo/blog'
import { Industries } from './pages/seo/industries'
import { Contact } from './pages/seo/contact'
import { Features } from './pages/seo/features'
import { Vendors } from './pages/seo/vendors'
import { Buyers } from './pages/seo/buyers'
import { Security } from './pages/seo/security'
import B2BMarketplace from './pages/seo/b2b-marketplace'
import B2BLeads from './pages/seo/b2b-leads'
import B2BLeadGeneration from './pages/seo/b2b-lead-generation'
import VendorSearch from './pages/seo/vendor-search'
import FindSuppliers from './pages/seo/find-suppliers'
import ThomasPageAlternative from './pages/thomaspage-alternative'
import EuropagesAlternative from './pages/europages-alternative'
import VendorProfile from './pages/vendor-profile'
import VendorShowcase from './pages/vendor-showcase'
import VendorSpotlightPage from './pages/vendor-spotlight'
import VendorSpotlightProfilePage from './pages/vendor-spotlight-profile'
import InvitationPage from './pages/invitation'

function App() {
  // Capture GCLID from URL parameters on app load
  React.useEffect(() => {
    captureGclidFromUrl()
  }, [])

  return (
    <ThemeProvider defaultTheme="light" storageKey="sourcedin-theme">
      <AuthProvider>
        <IntercomComponent />
        <Layout>
          <Switch>
            {/* SEO Pages - Public */}
            <Route path="/home" component={SEOHomepage} />
            <Route path="/about-us" component={AboutUs} />
            <Route path="/how-it-works" component={HowItWorks} />
            <Route path="/features" component={Features} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/success-stories" component={SuccessStories} />
            <Route path="/case-studies" component={SuccessStories} />
            <Route path="/blog" component={Blog} />
            <Route path="/resources" component={Blog} />
            <Route path="/industries" component={Industries} />
            <Route path="/solutions" component={Industries} />
            <Route path="/contact" component={Contact} />
            <Route path="/contact-us" component={Contact} />
            <Route path="/get-in-touch" component={Contact} />
            <Route path="/vendors" component={Vendors} />
            <Route path="/for-vendors" component={Vendors} />
            <Route path="/suppliers" component={Vendors} />
            <Route path="/buyers" component={Buyers} />
            <Route path="/for-buyers" component={Buyers} />
            <Route path="/procurement" component={Buyers} />
            <Route path="/security" component={Security} />
            <Route path="/compliance" component={Security} />
            <Route path="/data-protection" component={Security} />
            
            {/* New B2B SEO Pages */}
            <Route path="/b2b-marketplace" component={B2BMarketplace} />
            <Route path="/b2b-marketplace-platform" component={B2BMarketplace} />
            <Route path="/b2b-leads" component={B2BLeads} />
            <Route path="/b2b-lead-generation" component={B2BLeadGeneration} />
            <Route path="/b2b-lead-generation-strategies" component={B2BLeadGeneration} />
            <Route path="/vendor-search" component={VendorSearch} />
            <Route path="/supplier-search" component={VendorSearch} />
            <Route path="/vendor-discovery" component={VendorSearch} />
            <Route path="/find-suppliers" component={FindSuppliers} />
            <Route path="/thomaspage-alternative" component={ThomasPageAlternative} />
            <Route path="/thomasnet-alternative" component={ThomasPageAlternative} />
            <Route path="/thomas-alternative" component={ThomasPageAlternative} />
            <Route path="/europages-alternative" component={EuropagesAlternative} />
            <Route path="/europages-replacement" component={EuropagesAlternative} />
            <Route path="/europages-substitute" component={EuropagesAlternative} />
            
            {/* Authentication Pages */}
            <Route path="/login" component={Login} />
            <Route path="/register" component={Register} />
            <Route path="/reset-password-request" component={ResetPasswordRequest} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/terms" component={Terms} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/auth/callback" component={AuthCallback} />
            <Route path="/welcome">
              <ProtectedRoute>
                <Welcome />
              </ProtectedRoute>
            </Route>
            <Route path="/structured-data-test" component={StructuredDataTest} />
            <Route path="/">
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            </Route>
            <Route path="/request-for-proposal-marketplace" component={AllRfps} />
            <Route path="/request-for-proposal-marketplace/:category" component={AllRfps} />
            {/* Legacy redirects */}
            <Route path="/all-rfps" component={AllRfps} />
            <Route path="/all-rfps/:category" component={AllRfps} />
            <Route path="/rfps" component={AllRfps} />
            <Route path="/rfps/:category" component={AllRfps} />
            <Route path="/opportunities" component={AllRfps} />
            <Route path="/opportunities/:category" component={AllRfps} />
            <Route path="/create">
              <ProtectedRoute>
                <CreateRfp />
              </ProtectedRoute>
            </Route>
            <Route path="/edit-rfp/:id">
              <ProtectedRoute>
                <EditRfp />
              </ProtectedRoute>
            </Route>
            <Route path="/edit-response/:id">
              <ProtectedRoute>
                <EditResponse />
              </ProtectedRoute>
            </Route>
            <Route path="/rfp/:identifier" component={RfpDetail} />
            <Route path="/respond/:rfpId">
              <ProtectedRoute>
                <RespondToRfp />
              </ProtectedRoute>
            </Route>
            <Route path="/rfp/:rfpId/responses">
              <ProtectedRoute>
                <RfpResponses />
              </ProtectedRoute>
            </Route>

            <Route path="/profile">
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            </Route>
            <Route path="/vendor-profile">
              <ProtectedRoute>
                <VendorProfile />
              </ProtectedRoute>
            </Route>
            <Route path="/vendor-showcase" component={VendorShowcase} />
            <Route path="/vendor-spotlight" component={VendorSpotlightPage} />
            <Route path="/vendor-spotlight/:slug" component={VendorSpotlightProfilePage} />
            <Route path="/watchlist">
              <ProtectedRoute>
                <Watchlist />
              </ProtectedRoute>
            </Route>
            <Route path="/my-rfps">
              <ProtectedRoute>
                <MyRfps />
              </ProtectedRoute>
            </Route>
            <Route path="/my-responses">
              <ProtectedRoute>
                <MyResponses />
              </ProtectedRoute>
            </Route>
            
            {/* Invitation Page - Public */}
            <Route path="/invitation/:invitationId" component={InvitationPage} />
          </Switch>
        </Layout>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App 